import { useEffect, useMemo, useRef, useState } from 'react';
import { Stewardship } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, CopyValue, Facts, Field, Form } from '@beyond-js/ui/react';
import { Panel, Problem, useReauthenticate } from '@accounts/ui';

/** The service's rule for a short name: 3 to 30 lowercase letters, digits and single inner hyphens */
const rule = (value: string) => /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(value) && !value.includes('--');

/**
 * An organization's own settings: its name, its short name and its logo (ACC-20, D195), so it can be told
 * apart in every switcher of the family, and its identifier to copy. A name the person already uses for
 * another organization is warned about, and saved only when they say so. Products store the identifier, so
 * renaming changes what people read and nothing a product keyed on.
 *
 * Shown to owners and administrators; only an owner changes the short name, which asks for a recent proof
 * of identity, and the one it replaces stays reserved for the organization. The name and the short name
 * are saved together or not at all. An emptied field is a step not yet taken (D183): Save waits quietly and
 * a press puts the caret in it; only a short name that breaks its rule is said.
 */
export function Settings({ organization, name, slug, owner, logo, onSaved }: { organization: string; name: string; slug?: string; owner: boolean; logo?: string; onSaved: (notice: string) => Promise<void> }) {
	const { text, client, organizations, refresh, language } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const reauthenticate = useReauthenticate();
	const picker = useRef<HTMLInputElement>(null);

	const [draft, setDraft] = useState(name);
	const [short, setShort] = useState(slug ?? '');
	// What is being sent, so a wait is said only on the action that started it
	const [busy, setBusy] = useState<'save' | 'logo'>();
	const named = useRef<HTMLInputElement>(null);
	const [invalid, setInvalid] = useState<{ slug?: string }>({});
	const [failure, setFailure] = useState<unknown>();
	const [duplicate, setDuplicate] = useState(false);

	// The values can change underneath the form — another owner or administrator, or this save — and an
	// untouched draft should follow them
	useEffect(() => setDraft(name), [name]);
	useEffect(() => setShort(slug ?? ''), [slug]);

	const wanted = draft.trim();
	const next = short.trim().toLowerCase();
	const changes = { ...(wanted !== name ? { name: wanted } : {}), ...(owner && next !== (slug ?? '') ? { slug: next } : {}) };

	async function run(work: () => Promise<string>, kind: 'save' | 'logo' = 'save') {
		setBusy(kind);
		setFailure(undefined);
		try {
			await onSaved(await work());
			setDuplicate(false);
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return;
			if (Refusal.is(error) && error.code === 'NAME_DUPLICATE') setDuplicate(true);
			else if (Refusal.is(error) && error.code === 'SLUG_UNAVAILABLE') setInvalid({ slug: text('error.slug.unavailable') });
			else setFailure(error);
			// Nothing came back, so the change may have happened: what is shown is read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(undefined);
		}
	}

	function save(again = false) {
		// An empty field never leaves: `Form` keeps the caret in it, and editing the name takes the warning and its Save away
		if (!wanted || (owner && !next)) return;
		const problems = changes.slug !== undefined && !rule(changes.slug) ? { slug: text('settings.slug.invalid') } : {};
		setInvalid(problems);
		if (problems.slug) return;
		const send = () => organizations.change(organization, changes, again);
		void run(async () => {
			// A new short name asks for a recent proof of identity; once it is given, the same change goes again
			try {
				await send();
			} catch (error) {
				if (!(Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate()))) throw error;
				await send();
			}
			if (changes.slug !== undefined && changes.name === undefined && slug) return text('settings.slug.done', { previous: slug });
			return changes.slug !== undefined ? text('settings.saved') : text('settings.done');
		});
	}

	return (
		<Panel id="settings" title={text('settings.title')}>
			<Form
				className="bui-form-width flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					save();
				}}
			>
				{failure ? <Problem error={failure} /> : null}
				{duplicate ? (
					<Callout
						tone="warning"
						live
						title={text('organizations.duplicate', { name: wanted })}
						actions={
							<Button variant="secondary" busy={busy === 'save'} onClick={() => save(true)}>
								{text('settings.save.anyway')}
							</Button>
						}
					/>
				) : null}
				<Field label={text('organizations.name')}>
					<input
						ref={named}
						value={draft}
						maxLength={120}
						required
						onChange={event => {
							setDraft(event.target.value);
							// The warning names the name it was given: another one is asked again when saved
							setDuplicate(false);
						}}
					/>
				</Field>
				{owner ? (
					<Field label={text('settings.slug')} hint={text('settings.slug.help')} error={invalid.slug}>
						<input
							value={short}
							maxLength={30}
							required
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							onChange={event => {
								setShort(event.target.value);
								setInvalid({});
							}}
						/>
					</Field>
				) : null}
				<div>
					{/* Nothing changed yet: Save waits quietly, and a press puts the caret in the name (D183) */}
					<Button type="submit" variant="primary" busy={busy === 'save'} labels={{ busy: text('action.saving') }} waiting={!Object.keys(changes).length} place={() => named.current}>
						{text('action.save')}
					</Button>
				</div>
			</Form>

			<div className="mt-6 bui-form-width">
				<Facts
					label={text('settings.title')}
					rows={[
						owner || !slug ? null : { key: 'slug', label: text('settings.slug'), value: slug },
						{ key: 'id', label: text('settings.id'), value: <CopyValue value={organization} label={text('settings.id')} cut="middle" labels={CopyValue.labels[language]} /> }
					]}
				/>
				{owner || !slug ? null : <p className="mt-2 text-small text-ink-muted">{text('settings.slug.owners')}</p>}
			</div>

			<div className="mt-6 flex flex-wrap items-center gap-4">
				{logo ? <img src={logo} alt={text('settings.logo')} className="h-12 w-12 rounded-beyond border border-line object-cover" /> : null}
				<div className="flex flex-col gap-1">
					<p className="text-body text-ink">{text('settings.logo')}</p>
					<p className="text-small text-ink-muted">{text('settings.logo.help')}</p>
					<div className="flex flex-wrap gap-2">
						<input
							ref={picker}
							type="file"
							accept="image/png,image/jpeg,image/webp"
							className="sr-only"
							onChange={event => {
								const file = event.target.files?.[0];
								if (file) void run(async () => (await stewardship.logo(organization, file), text('settings.logo.done')), 'logo');
								event.target.value = '';
							}}
						/>
						<Button variant="secondary" small busy={busy === 'logo'} onClick={() => picker.current?.click()}>
							{text('settings.logo.choose')}
						</Button>
						{logo ? (
							<Button variant="quiet" small disabled={busy === 'logo'} onClick={() => void run(async () => (await stewardship.unlogo(organization), text('settings.logo.done')), 'logo')}>
								{text('settings.logo.remove')}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		</Panel>
	);
}
